import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import './i18n'; // Initialize i18n

import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider, useAuth } from './Contexts/authContext';
import { ColorProvider } from './Contexts/ColorContext';
import { ClubProvider } from './Contexts/ClubContext';
import { LanguageProvider } from './Contexts/LanguageContext';
import './styles/App.css';
import { ROUTES } from './constants/routes';
import LanguageWrapper from './components/LanguageWrapper';
import LegacyRedirect from './components/LegacyRedirect';
import { cleanPath } from './utils/routeUtils';
import Layout from './components/Layout';
import axios from 'axios';

// Lazy loading components for performance
// Eager load public pages for better SEO and LCP
import Home from './pages/Home';
import Modalidades from './pages/Modalidades';
import Funcionalidades from './pages/Funcionalidades';
import Contacto from './pages/Contacto';
import Preco from './pages/Preco';
const Login = React.lazy(() => import('./components/Login'));
const SportLandingPage = React.lazy(() => import('./pages/SportLandingPage'));
const RegisterComplete = React.lazy(() => import('./components/RegisterComplete'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const Termos = React.lazy(() => import('./pages/Termos'));
const Politica = React.lazy(() => import('./pages/Politica'));
const FichaClubeFutebol = React.lazy(() => import('./sports/football/FichaClubeFutebol'));
const PlantelFootball = React.lazy(() => import('./sports/football/PlantelFootball'));
const PlaneamentoFootball = React.lazy(() => import('./sports/football/PlaneamentoFootball'));
const RegistoAnualFootball = React.lazy(() => import('./sports/football/RegistoAnualFootball'));
const ExerciciosFootball = React.lazy(() => import('./sports/football/ExerciciosFootball'));
const PlanosTreinoFootball = React.lazy(() => import('./sports/football/PlanosTreinoFootball'));
const AssiduidadeFootball = React.lazy(() => import('./sports/football/AssiduidadeFootball'));
const CompeticaoFootball = React.lazy(() => import('./sports/football/CompeticaoFootball'));
const ConvocatoriaFootball = React.lazy(() => import('./sports/football/ConvocatoriaFootball'));
const ModeloJogoFootball = React.lazy(() => import('./sports/football/ModeloJogoFootball'));
const JogadasEnsaiadasFootball = React.lazy(() => import('./sports/football/JogadasEnsaiadasFootball'));
const PreparacaoJogoFootball = React.lazy(() => import('./sports/football/PreparacaoJogoFootball'));
const AnaliseReflexaoFootball = React.lazy(() => import('./sports/football/AnaliseReflexaoFootball'));
const AdversariosFootball = React.lazy(() => import('./sports/football/AdversariosFootball'));
const OlheiroFootball = React.lazy(() => import('./sports/football/OlheiroFootball'));
const AnaliseJogoDirecta = React.lazy(() => import('./sports/football/AnaliseJogoDirecta'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const ThankYou = React.lazy(() => import('./pages/ThankYou'));


// Loading component
const LoadingSpinner = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '80vh',
        width: '100%'
    }}>
        <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(59, 130, 246, 0.1)',
            borderTop: '3px solid #3B82F6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }} />
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

// Global Axios configuration
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';

const HomeRedirect = () => {
    const { user } = useAuth();
    return user ? <Navigate to={ROUTES.FICHA_CLUBE} replace /> : <Home />;
};

const AppRoutes = () => {
    // Helper to get default language safely
    const getDefaultLang = () => {
        return localStorage.getItem('selectedLanguage') || 'en';
    };

    return (
        <React.Suspense fallback={<LoadingSpinner />}>
            <Routes>
                {/* Rota raiz absoluta redireciona para /{lang} */}
                <Route path="/" element={<Navigate to={`/${getDefaultLang()}`} replace />} />

                {/* Rotas com prefixo de idioma */}
                <Route path="/:lang" element={<LanguageWrapper />}>
                    <Route index element={<HomeRedirect />} />

                    <Route path={cleanPath(ROUTES.MODALIDADES)} element={<Modalidades />} />
                    <Route path={cleanPath(ROUTES.FUNCIONALIDADES)} element={<Funcionalidades />} />
                    <Route path={cleanPath(ROUTES.CONTACTO)} element={<Contacto />} />
                    <Route path={cleanPath(ROUTES.PRECO)} element={<Preco />} />
                    <Route path={cleanPath(ROUTES.TERMS)} element={<Termos />} />
                    <Route path={cleanPath(ROUTES.POLICY)} element={<Politica />} />
                    <Route path="login" element={<Login />} />
                    <Route path="register" element={<RegisterComplete />} />
                    <Route path="thank-you" element={<ThankYou />} />
                    <Route path="reset-password/:token" element={<ResetPassword />} />

                    {/* SEO Landing Pages */}
                    <Route path="football-coaching-software" element={<SportLandingPage sportKey="football" />} />
                    <Route path="futsal-coaching-software" element={<SportLandingPage sportKey="futsal" />} />
                    <Route path="volleyball-coaching-software" element={<SportLandingPage sportKey="volleyball" />} />
                    <Route path="handball-coaching-software" element={<SportLandingPage sportKey="handball" />} />
                    <Route path="basketball-coaching-software" element={<SportLandingPage sportKey="basketball" />} />

                    {/* Football routes */}
                    <Route path={cleanPath(ROUTES.FICHA_CLUBE)} element={<ProtectedRoute><FichaClubeFutebol /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.PLANTEL)} element={<ProtectedRoute><PlantelFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.PLANEJAMENTO)} element={<ProtectedRoute><PlaneamentoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.REGISTO_ANUAL)} element={<ProtectedRoute><RegistoAnualFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.EXERCICIOS)} element={<ProtectedRoute><ExerciciosFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.PLANOS_TREINO)} element={<ProtectedRoute><PlanosTreinoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.ASSIDUIDADE)} element={<ProtectedRoute><AssiduidadeFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.MODELO_JOGO)} element={<ProtectedRoute><ModeloJogoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.JOGADAS_ENSAIADAS)} element={<ProtectedRoute><JogadasEnsaiadasFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.COMPETICAO)} element={<ProtectedRoute><CompeticaoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.PREPARACAO_JOGO)} element={<ProtectedRoute><PreparacaoJogoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.CONVOCATORIA)} element={<ProtectedRoute><ConvocatoriaFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.ANALISE_REFLEXAO)} element={<ProtectedRoute><AnaliseReflexaoFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.ADVERSARIOS)} element={<ProtectedRoute><AdversariosFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.OLHEIRO)} element={<ProtectedRoute><OlheiroFootball /></ProtectedRoute>} />
                    <Route path={cleanPath(ROUTES.ANALISE_DIRECTA)} element={<ProtectedRoute><AnaliseJogoDirecta /></ProtectedRoute>} />

                    {/* Admin Route */}
                    <Route path="admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
                </Route>

                {/* Catch-all para rotas antigas sem idioma (ex: /club-sheet) */}
                <Route path="*" element={<LegacyRedirect />} />
            </Routes>
        </React.Suspense>
    );
};

const App = () => {
    // Ping de aquecimento para acordar o Render (Backend)
    React.useEffect(() => {
        const warmUpBackend = async () => {
            try {
                console.log('A enviar ping de aquecimento ao backend...');
                await axios.get('/');
                console.log('Backend respondeu ao ping!');
            } catch (error) {
                // Ignorar erros silenciosamente (comum se estiver a acordar)
                console.log('Ping de aquecimento falhou/timeout (normal se estiver a dormir):', error.message);
            }
        };

        warmUpBackend();
    }, []);

    return (
        <GoogleOAuthProvider clientId="582436700326-pip8luv2n6qqes12a6oeh7021hfa2o8h.apps.googleusercontent.com">
            <AuthProvider>
                <HelmetProvider>
                    <Router>
                        <LanguageProvider>
                            <ColorProvider>
                                <ClubProvider>
                                    <ScrollToTop />
                                    <Layout>
                                        <AppRoutes />
                                    </Layout>
                                </ClubProvider>
                            </ColorProvider>
                        </LanguageProvider>
                    </Router>
                </HelmetProvider>
            </AuthProvider>
        </GoogleOAuthProvider>
    );
};

export default App;
